import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "../../../components/Button";
import { api } from "../../../services/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApp } from "../../../services/app-context";

import { ShowToast } from "../../../services/toast";
import { twMerge } from "tailwind-merge";
import { getLabel } from "../utils/labels";
import SideModal from "./SideModal";
import { CertificationAssignUserStep } from "./CertificationAssignUserStep";

function normalizeAssignmentsList(res: unknown): any[] {
  const body = (res as { data?: unknown })?.data;
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object" && Array.isArray((body as { data?: unknown }).data)) {
    return (body as { data: any[] }).data;
  }
  return [];
}

export const UserAssignmentModal = ({
  onClose,
  isOpen,
  onSuccess,
  certification,
}: {
  onClose: () => void;
  isOpen: boolean;
  onSuccess: () => void;
  certification?: any;
}) => {
  const queryClient = useQueryClient();
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const certificationSingular = getLabel("certification.singular", labels);

  const { data: assignmentsData, isFetched: assignmentsFetched, dataUpdatedAt } = useQuery({
    queryKey: ["certification-assignments-prefill", certification?.id],
    queryFn: () => api.getAllAssignments(certification!.id),
    enabled: !!certification?.id && isOpen,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const { mutate: putAssignments, isLoading } = useMutation({
    mutationFn: ({ certificationId, users }: { certificationId: string; users: string[] }) =>
      api.putCertificationAssignments(certificationId, { users }),
    onSuccess: () => {
      ShowToast({
        message: `Assignees updated successfully`,
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["certification-assignments-prefill", certification?.id] });
      queryClient.invalidateQueries({ queryKey: ["certification-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["certifications-list"] });
      onClose();
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || `Failed to update ${certificationSingular.toLowerCase()} assignees`;
      ShowToast({
        message: errorMessage,
        type: "error",
      });
    },
  });

  const getInitialForm = () => ({
    userList: [] as { label: string; value: string }[],
    certificationId: certification?.id as string | undefined,
  });

  const [form, setForm] = useState<any>(getInitialForm);

  const prevOpenRef = useRef(false);
  /** Last `dataUpdatedAt` applied into the form; avoids blocking when RQ shows cached rows first then updates after `refetchOnMount: "always"`. */
  const lastPrefilledAssignmentsDataUpdatedAtRef = useRef<number>(-1);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setForm(getInitialForm());
    }
    if (isOpen) {
      lastPrefilledAssignmentsDataUpdatedAtRef.current = -1;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, certification?.id]);

  useEffect(() => {
    if (!isOpen || !certification?.id) return;
    if (!assignmentsFetched) return;
    if (dataUpdatedAt <= lastPrefilledAssignmentsDataUpdatedAtRef.current) return;
    const rows = normalizeAssignmentsList(assignmentsData);
    const userList = rows?.map((row: any) => ({ label: row.userName || row.name || row.userID, value: row.userID }));
    setForm((prev: any) => ({ ...prev, userList }));
    lastPrefilledAssignmentsDataUpdatedAtRef.current = dataUpdatedAt;
  }, [isOpen, certification?.id, assignmentsFetched, assignmentsData, dataUpdatedAt]);

  const handleSave = () => {
    if (!certification?.id) return;
    const users = (form.userList || []).map((u: { value: string }) => u.value);
    putAssignments({ certificationId: certification.id, users });
  };

  const isDisabled = useMemo(() => {
    return !form.userList?.length;
  }, [form.userList]);

  return (
    <SideModal
      show={isOpen}
      toggle={onClose}
      title={`Add/Remove Assignees`}
      containerClass="flex flex-col"
    >
      <div className="mb-4" />
      <div className="flex flex-col gap-4 max-h-[75vh] overflow-auto">
        <CertificationAssignUserStep form={form} setForm={setForm} />
      </div>
      <div className={twMerge("flex !justify-end w-full flex-1 items-center")}>
        <Button
          onClick={handleSave}
          variant="primary"
          className={`h-[37px] w-[150px] !text-lg !font-normal !rounded-full ${isLoading ? "!w-[150px]" : "!w-[117px]"}`}
          loading={isLoading}
          disabled={isDisabled || isLoading}
        >
          Save
        </Button>
      </div>
    </SideModal>
  );
};
